/**
 * Shared graph visual theme — single source of truth for the Knowledge-Graph
 * color palette, legend groupings and edge styling.
 *
 * This consolidates the palette that used to be duplicated in
 * `ReactFlowSchemaGraph.tsx` (`NODE_COLORS`) and `SchemaLegend.tsx`
 * (`ALL_LEGEND_ITEMS`), and is also consumed by the vis-network
 * `SchemaVisualizer` so both Step-4 renderers stay visually consistent.
 */

export interface NodeColor {
  bg: string;
  border: string;
  text: string;
}

export interface LegendItem extends NodeColor {
  name: string;
}

/**
 * Every category / KG-area color, keyed by the exact name used on nodes.
 * `default` and `central` are always present so lookups can fall back safely.
 */
export const LEGEND_ITEMS: Record<string, LegendItem> = {
  // Master Data Hubs (blue tones — foundational entities)
  'Customer Hub': { name: 'Customer Hub', bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  'Product Hub': { name: 'Product Hub', bg: '#e0f2fe', border: '#0ea5e9', text: '#075985' },
  'Supplier Hub': { name: 'Supplier Hub', bg: '#cffafe', border: '#06b6d4', text: '#155e75' },
  'Employee Hub': { name: 'Employee Hub', bg: '#c7d2fe', border: '#818cf8', text: '#4338ca' },
  'Material Hub': { name: 'Material Hub', bg: '#a5f3fc', border: '#22d3ee', text: '#0e7490' },
  'Asset Hub': { name: 'Asset Hub', bg: '#bae6fd', border: '#38bdf8', text: '#0369a1' },
  'Location Hub': { name: 'Location Hub', bg: '#f1f5f9', border: '#64748b', text: '#334155' },

  // Transactional Processes (warm tones — business flows)
  'Order-to-Cash': { name: 'Order-to-Cash', bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  'Procure-to-Pay': { name: 'Procure-to-Pay', bg: '#fed7aa', border: '#f97316', text: '#9a3412' },
  'Plan-to-Produce': { name: 'Plan-to-Produce', bg: '#fecaca', border: '#ef4444', text: '#991b1b' },
  'Hire-to-Retire': { name: 'Hire-to-Retire', bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  'Record-to-Report': { name: 'Record-to-Report', bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' },
  'Inventory Management': { name: 'Inventory Management', bg: '#d1fae5', border: '#10b981', text: '#065f46' },

  // Analytical Layer (green / purple tones — insights & metrics)
  'Metrics & KPIs': { name: 'Metrics & KPIs', bg: '#dcfce7', border: '#22c55e', text: '#166534' },
  'Financial Analytics': { name: 'Financial Analytics', bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },
  'Supply Chain Analytics': { name: 'Supply Chain Analytics', bg: '#ccfbf1', border: '#14b8a6', text: '#0f766e' },
  'Customer Analytics': { name: 'Customer Analytics', bg: '#f3e8ff', border: '#a855f7', text: '#6b21a8' },
  'Sales Analytics': { name: 'Sales Analytics', bg: '#fae8ff', border: '#d946ef', text: '#86198f' },
  'Operational Analytics': { name: 'Operational Analytics', bg: '#d9f99d', border: '#84cc16', text: '#3f6212' },

  // Cross-Functional (neutral tones — connecting entities)
  'Business Calendar': { name: 'Business Calendar', bg: '#f5f5f5', border: '#737373', text: '#404040' },
  'Organizational Structure': { name: 'Organizational Structure', bg: '#e5e7eb', border: '#6b7280', text: '#374151' },
  'Location & Geography': { name: 'Location & Geography', bg: '#f1f5f9', border: '#64748b', text: '#334155' },
  'Document Management': { name: 'Document Management', bg: '#fef2f2', border: '#f87171', text: '#b91c1c' },

  // Traditional ERP modules
  Sales: { name: 'Sales', bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  Inventory: { name: 'Inventory', bg: '#d1fae5', border: '#10b981', text: '#065f46' },
  Finance: { name: 'Finance', bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' },
  HR: { name: 'HR', bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  Production: { name: 'Production', bg: '#fed7aa', border: '#f97316', text: '#9a3412' },
  Logistics: { name: 'Logistics', bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },
  Customer: { name: 'Customer', bg: '#cffafe', border: '#06b6d4', text: '#155e75' },

  // Special
  central: { name: 'Central/Hub Entity', bg: '#fef08a', border: '#eab308', text: '#713f12' },
  default: { name: 'Other', bg: '#f0f9ff', border: '#0ea5e9', text: '#0c4a6e' },
};

/** Bare color map (no `name`) — the shape the renderers pass to nodes. */
export const PALETTE: Record<string, NodeColor> = Object.fromEntries(
  Object.entries(LEGEND_ITEMS).map(([key, item]) => [
    key,
    { bg: item.bg, border: item.border, text: item.text },
  ])
);

/** Safe color lookup with fallback to the `default` swatch. */
export function getNodeColors(category?: string | null): NodeColor {
  if (category && PALETTE[category]) return PALETTE[category];
  return PALETTE.default;
}

/** Legend groupings — used to build the dynamic color legend. */
export const CATEGORY_DEFINITIONS: { title: string; description: string; keys: string[] }[] = [
  {
    title: 'Master Data Hubs',
    description: 'Core business entities',
    keys: ['Customer Hub', 'Product Hub', 'Supplier Hub', 'Employee Hub', 'Material Hub', 'Asset Hub', 'Location Hub'],
  },
  {
    title: 'Transactional Processes',
    description: 'Business operations',
    keys: ['Order-to-Cash', 'Procure-to-Pay', 'Plan-to-Produce', 'Hire-to-Retire', 'Record-to-Report', 'Inventory Management'],
  },
  {
    title: 'Analytical Layer',
    description: 'Insights & metrics',
    keys: ['Metrics & KPIs', 'Financial Analytics', 'Supply Chain Analytics', 'Customer Analytics', 'Sales Analytics', 'Operational Analytics'],
  },
  {
    title: 'Traditional Modules',
    description: 'ERP system modules',
    keys: ['Sales', 'Inventory', 'Finance', 'HR', 'Production', 'Logistics', 'Customer'],
  },
  {
    title: 'Special',
    description: 'Visual indicators',
    keys: ['central'],
  },
];

/** Shared edge styling tokens (slate for structural, emerald for semantic/new). */
export const EDGE_STYLE = {
  base: '#94a3b8', // slate-400
  highlight: '#475569', // slate-600
  semantic: '#10b981', // emerald-500 (KG-First / newly added relationships)
  missing: '#f97316', // orange-500 (missing-from-evaluation)
  label: '#334155', // slate-700
};

/** Node sizing — used for degree-based emphasis of hub entities. */
export const NODE_SIZE = {
  min: 26,
  max: 54,
  base: 32,
};

/**
 * Scale a node's visual size by its connection degree so hubs stand out.
 * Returns a value between NODE_SIZE.min and NODE_SIZE.max.
 */
export function sizeForDegree(degree: number, maxDegree: number): number {
  if (maxDegree <= 0) return NODE_SIZE.base;
  const t = Math.min(1, degree / maxDegree);
  return Math.round(NODE_SIZE.min + t * (NODE_SIZE.max - NODE_SIZE.min));
}
